import React from 'react';
import {
  Box,
  Button,
  Radio,
} from '@mui/material';
import {
  AlignmentType,
  BorderStyle,
  Document,
  ImageRun,
  Paragraph,
  TextRun,
  Packer,
  Table as DocxTable,
  TableRow as DocxTableRow,
  TableCell as DocxTableCell,
  WidthType,
} from 'docx';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Download from '../../images/managedata/download.svg';
import Pdf from '../../images/managedata/pdf.svg';
import Word from '../../images/managedata/word.svg';
import Csv from '../../images/managedata/csv.svg';
import RouteLogo from '../../images/image-transparent.png';

const chartPalette = ["#2563EB", "#22C55E", "#8B5CF6", "#F59E0B", "#20A8E0", "#64748B"];

const getNiceChartStep = (rawStep) => {
  if (!Number.isFinite(rawStep) || rawStep <= 0) return 1;
  const exponent = Math.floor(Math.log10(rawStep));
  const magnitude = 10 ** exponent;
  const normalized = rawStep / magnitude;
  const niceNormalized = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  return niceNormalized * magnitude;
};

const getChartAxisScale = (maxValue) => {
  const step = getNiceChartStep((Number(maxValue) || 1) / 5);
  const axisMax = Math.max(step, step * Math.ceil((Number(maxValue) || 1) / step));
  const tickCount = Math.floor(axisMax / step);
  return {
    axisMax,
    ticks: Array.from({ length: tickCount + 1 }, (_, index) => {
      const value = axisMax - index * step;
      return Number.isInteger(value) ? `${value}` : value.toFixed(1);
    }),
  };
};

const formatChartTick = (value) => (Number.isInteger(value) ? `${value}` : value.toFixed(1));

const getChartAxisLabels = (chart) => {
  const title = String(chart?.title || "").toLowerCase();
  if (title.includes("overspeed")) return { x: "Vehicles", y: "Incidents" };
  if (title.includes("speed")) return { x: "Vehicles", y: chart?.unit || "Speed" };
  if (title.includes("route")) return { x: "Routes", y: "Vehicles" };
  if (title.includes("stop")) return { x: "Stops", y: "Visits" };
  if (title.includes("vehicle")) return { x: "Vehicles", y: "Trips" };
  if (title.includes("status")) return { x: "Status", y: "Count" };
  return { x: "Items", y: chart?.unit || "Value" };
};

const ExportOptions = ({ reportData, exportFormat, setExportFormat }) => {
  const getReportSections = (data) =>
    data?.sections?.length ? data.sections : [data];
  const getReportTitle = (data) =>
    data?.previewTitle || `${data.reportType} Report`;
  const getPreviewDetails = (data) => data?.previewDetails || [];
  const getPreviewHeaderDetails = (data) =>
    data?.previewHeaderDetails || getPreviewDetails(data).slice(0, 3);
  const getPreviewFilterDetails = (data) =>
    data?.previewFilterDetails || getPreviewDetails(data).slice(3, 6);
  const getPreviewMetrics = (data) => data?.previewMetrics || [];
  const getPreviewCharts = (data) => data?.previewCharts || [];
  const hexToRgb = (hex, fallback = [37, 99, 235]) => {
    const normalized = String(hex || "").replace("#", "");
    if (normalized.length !== 6) return fallback;
    const value = parseInt(normalized, 16);
    if (Number.isNaN(value)) return fallback;
    return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
  };
  const getChartColor = (chart, item) => hexToRgb(item?.color || chart?.color);
  const getExportCellValue = (value) => {
    if (value === null || value === undefined) return "NA";
    if (Array.isArray(value)) {
      return value.length
        ? value.map((item) => item?.scheduleId || item?.name || item).join(", ")
        : "No Schedules";
    }
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
  };
  const escapeHtml = (value) =>
    String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const loadImageAsDataUrl = (src) =>
    new Promise((resolve) => {
      const image = new Image();
      image.crossOrigin = "anonymous";
      image.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = image.naturalWidth;
        canvas.height = image.naturalHeight;
        const context = canvas.getContext("2d");
        context.drawImage(image, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      };
      image.onerror = () => resolve(null);
      image.src = src;
    });

  const loadImageArrayBuffer = async (src) => {
    try {
      const response = await fetch(src);
      return response.arrayBuffer();
    } catch (error) {
      return null;
    }
  };

  const getDonutSegments = (items) => {
    const values = (items || [])
      .slice(0, 5)
      .map((item, index) => ({
        ...item,
        value: Number(item.value) || 0,
        color: item.color || chartPalette[index % chartPalette.length],
      }))
      .filter((item) => item.value > 0);
    const total = values.reduce((sum, item) => sum + item.value, 0);
    let cursor = 0;

    if (!total) return [];

    return values.map((item) => {
      const start = cursor;
      const end = start + (item.value / total) * 100;
      cursor = end;
      return {
        ...item,
        percent: Math.round((item.value / total) * 100),
        start,
        end,
      };
    });
  };

  const createDonutChartDataUrl = (chart) => {
    const segments = getDonutSegments(chart.items);
    if (!segments.length) return null;

    const size = 240;
    const center = size / 2;
    const outerRadius = 112;
    const innerRadius = 66;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const context = canvas.getContext("2d");
    if (!context) return null;

    segments.forEach((segment) => {
      const startAngle = -Math.PI / 2 + (segment.start / 100) * Math.PI * 2;
      const endAngle = -Math.PI / 2 + (segment.end / 100) * Math.PI * 2;
      context.beginPath();
      context.moveTo(center, center);
      context.arc(center, center, outerRadius, startAngle, endAngle);
      context.closePath();
      context.fillStyle = segment.color;
      context.fill();
    });

    context.globalCompositeOperation = "destination-out";
    context.beginPath();
    context.arc(center, center, innerRadius, 0, Math.PI * 2);
    context.fill();
    context.globalCompositeOperation = "source-over";

    return canvas.toDataURL("image/png");
  };

  const dataUrlToUint8Array = (dataUrl) => {
    const base64 = String(dataUrl || "").split(",")[1];
    if (!base64) return null;
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }
    return bytes;
  };

  const drawCanvasText = (context, text, x, y, options = {}) => {
    context.fillStyle = options.color || "#001F52";
    context.font = `${options.weight || 500} ${options.size || 12}px ${options.family || "Inter, Arial, sans-serif"}`;
    context.textAlign = options.align || "left";
    context.textBaseline = options.baseline || "alphabetic";
    const value = String(text ?? "");
    const maxWidth = options.maxWidth;
    if (!maxWidth || context.measureText(value).width <= maxWidth) {
      context.fillText(value, x, y);
      return;
    }
    let output = value;
    while (output.length > 1 && context.measureText(`${output}...`).width > maxWidth) {
      output = output.slice(0, -1);
    }
    context.fillText(`${output}...`, x, y);
  };

  const createReportChartDataUrl = (chart) => {
    const width = 520;
    const height = 300;
    const canvas = document.createElement("canvas");
    canvas.width = width * 2;
    canvas.height = height * 2;
    const context = canvas.getContext("2d");
    if (!context) return null;
    context.scale(2, 2);

    context.fillStyle = "#FFFFFF";
    context.fillRect(0, 0, width, height);
    context.strokeStyle = "#D8DEE8";
    context.lineWidth = 1;
    context.beginPath();
    context.roundRect(1, 1, width - 2, height - 2, 8);
    context.stroke();
    drawCanvasText(context, chart.title, 22, 34, {
      size: 15,
      weight: 800,
      color: "#001F52",
      maxWidth: width - 44,
    });

    const items = chart.items || [];
    const maxValue = Math.max(
      chart.maxValue || 0,
      chart.threshold || 0,
      ...items.map((item) => Number(item.value) || 0),
      1
    );

    if (!items.length) {
      drawCanvasText(context, "No chart data", width / 2, height / 2, {
        align: "center",
        color: "#94A3B8",
        size: 13,
      });
      return canvas.toDataURL("image/png");
    }

    if (chart.type === "vertical") {
      const { axisMax, ticks } = getChartAxisScale(maxValue);
      const axisLabels = getChartAxisLabels(chart);
      const plot = { left: 74, top: 64, right: width - 30, bottom: 225 };
      const plotHeight = plot.bottom - plot.top;
      context.strokeStyle = "#D8DEE8";
      context.beginPath();
      context.moveTo(plot.left, plot.top);
      context.lineTo(plot.left, plot.bottom);
      context.lineTo(plot.right, plot.bottom);
      context.stroke();
      drawCanvasText(context, axisLabels.y, 28, 150, {
        size: 11,
        weight: 700,
        color: "#64748B",
        align: "center",
      });
      ticks.forEach((tick, index) => {
        const y = plot.top + (index / Math.max(ticks.length - 1, 1)) * plotHeight;
        drawCanvasText(context, tick, plot.left - 14, y + 4, {
          size: 11,
          color: "#94A3B8",
          align: "right",
        });
      });
      items.slice(0, Number(chart.maxItems) > 0 ? Number(chart.maxItems) : 4).forEach((item, index, visibleItems) => {
        const slot = (plot.right - plot.left) / Math.max(visibleItems.length, 1);
        const barHeight = Math.max(4, ((Number(item.value) || 0) / axisMax) * plotHeight);
        const barWidth = Math.min(34, Math.max(8, slot - 8));
        const x = plot.left + slot * index + slot / 2 - barWidth / 2;
        const y = plot.bottom - barHeight;
        context.fillStyle = item.color || chart.color || "#2563EB";
        context.beginPath();
        context.roundRect(x, y, barWidth, barHeight, 3);
        context.fill();
        drawCanvasText(context, item.label, x + barWidth / 2, plot.bottom + 22, {
          size: 11,
          color: "#64748B",
          align: "center",
          maxWidth: slot - 8,
        });
      });
      drawCanvasText(context, axisLabels.x, (plot.left + plot.right) / 2, height - 28, {
        size: 11,
        weight: 700,
        color: "#64748B",
        align: "center",
      });
    } else if (chart.type === "donut") {
      const segments = getDonutSegments(items);
      const total = segments.reduce((sum, item) => sum + item.value, 0);
      const centerX = 145;
      const centerY = 160;
      const outerRadius = 68;
      const innerRadius = 40;
      segments.forEach((segment) => {
        const startAngle = -Math.PI / 2 + (segment.start / 100) * Math.PI * 2;
        const endAngle = -Math.PI / 2 + (segment.end / 100) * Math.PI * 2;
        context.beginPath();
        context.moveTo(centerX, centerY);
        context.arc(centerX, centerY, outerRadius, startAngle, endAngle);
        context.closePath();
        context.fillStyle = segment.color;
        context.fill();
      });
      context.fillStyle = "#FFFFFF";
      context.beginPath();
      context.arc(centerX, centerY, innerRadius, 0, Math.PI * 2);
      context.fill();
      drawCanvasText(context, total, centerX, centerY - 4, {
        size: 24,
        weight: 800,
        align: "center",
        color: "#001F52",
      });
      drawCanvasText(context, "Total", centerX, centerY + 18, {
        size: 10,
        weight: 700,
        align: "center",
        color: "#64748B",
      });
      segments.forEach((item, index) => {
        const y = 98 + index * 28;
        context.fillStyle = item.color;
        context.beginPath();
        context.arc(285, y - 4, 4, 0, Math.PI * 2);
        context.fill();
        drawCanvasText(context, `${item.label} - ${item.value} (${item.percent}%)`, 298, y, {
          size: 12,
          color: "#001F52",
          maxWidth: 190,
        });
      });
    } else {
      const { axisMax } = getChartAxisScale(maxValue);
      const axisTicks = [0, axisMax / 2, axisMax].map(formatChartTick);
      const left = 28;
      const right = width - 28;
      const trackWidth = right - left;
      items.slice(0, 5).forEach((item, index) => {
        const y = 76 + index * 38;
        const percent = Math.min(100, ((Number(item.value) || 0) / axisMax) * 100);
        drawCanvasText(context, item.label, left, y, {
          size: 12,
          weight: 700,
          color: "#001F52",
          maxWidth: 360,
        });
        drawCanvasText(context, `${item.value}${chart.unit || ""}`, right, y, {
          size: 12,
          weight: 800,
          color: "#001F52",
          align: "right",
        });
        context.fillStyle = "#EEF2F7";
        context.beginPath();
        context.roundRect(left, y + 10, trackWidth, 7, 4);
        context.fill();
        context.fillStyle = item.color || chart.color || "#2563EB";
        context.beginPath();
        context.roundRect(left, y + 10, (trackWidth * percent) / 100, 7, 4);
        context.fill();
      });
      axisTicks.forEach((tick, index) => {
        const x = left + (index / 2) * trackWidth;
        drawCanvasText(context, tick, x, height - 26, {
          size: 11,
          color: "#94A3B8",
          align: index === 0 ? "left" : index === 2 ? "right" : "center",
        });
      });
    }

    return canvas.toDataURL("image/png");
  };

  const addPdfPageIfNeeded = (doc, y, neededHeight = 20) => {
    if (y + neededHeight <= 285) return y;
    doc.addPage();
    return 15;
  };

  const drawPdfPreviewDetails = (doc, data, startY) => {
    const details = getPreviewDetails(data);
    let y = startY;
    const cardWidth = 46;
    const gap = 5;
    const startX = 13;

    details.slice(0, 4).forEach((detail, index) => {
      const x = startX + index * (cardWidth + gap);

      doc.setTextColor(100, 116, 144);
      doc.setFontSize(7);
      doc.text(String(detail.label), x, y);
      doc.setTextColor(0, 31, 82);
      doc.setFontSize(8);
      doc.setFont(undefined, "bold");
      doc.text(String(detail.value || "N/A"), x, y + 5, { maxWidth: cardWidth });
      doc.setFont(undefined, "normal");
    });

    doc.setDrawColor(216, 222, 232);
    doc.line(13, y + 14, 197, y + 14);
    return y + 22;
  };

  const drawPdfMetrics = (doc, data, startY) => {
    const metrics = getPreviewMetrics(data);
    if (!metrics.length) return startY;
    let y = addPdfPageIfNeeded(doc, startY, 24);
    const width = 46;

    metrics.slice(0, 4).forEach((metric, index) => {
      const x = 13 + index * width;
      doc.setDrawColor(216, 222, 232);
      doc.setFillColor(248, 250, 252);
      doc.rect(x, y, width, 18, "FD");
      doc.setTextColor(100, 116, 144);
      doc.setFontSize(6.5);
      doc.text(String(metric.label), x + 3, y + 5, { maxWidth: width - 6 });
      doc.setTextColor(0, 31, 82);
      doc.setFontSize(14);
      doc.setFont(undefined, "bold");
      doc.text(`${metric.value}${metric.unit ? ` ${metric.unit}` : ""}`, x + 3, y + 13, {
        maxWidth: width - 6,
      });
      doc.setFont(undefined, "normal");
    });

    return y + 27;
  };

  const drawPdfFilterRow = (doc, data, startY) => {
    const filters = getPreviewDetails(data).slice(3, 6);
    if (!filters.length) return startY;
    let x = 13;
    doc.setTextColor(100, 116, 144);
    doc.setFontSize(7);
    filters.forEach((detail) => {
      const label = `${detail.label}: `;
      doc.text(label, x, startY);
      x += doc.getTextWidth(label);
      doc.setTextColor(0, 31, 82);
      doc.setFont(undefined, "bold");
      const value = String(detail.value || "N/A");
      doc.text(value, x, startY, { maxWidth: 52 });
      x += Math.min(58, doc.getTextWidth(value) + 10);
      doc.setFont(undefined, "normal");
      doc.setTextColor(100, 116, 144);
    });
    doc.setDrawColor(216, 222, 232);
    doc.line(13, startY + 6, 197, startY + 6);
    return startY + 13;
  };

  const drawPdfCharts = (doc, charts, startY) => {
    if (!charts.length) return startY;

    let y = addPdfPageIfNeeded(doc, startY, 70);
    const cardWidth = 56;
    const cardHeight = 64;
    const gap = 8;
    const startX = 13;

    doc.setTextColor(0, 31, 82);
    doc.setFontSize(10);
    doc.setFont(undefined, "bold");
    doc.text("Performance Breakdown", startX, y);
    doc.setDrawColor(216, 222, 232);
    doc.line(startX, y + 3, 197, y + 3);
    doc.setFont(undefined, "normal");
    y += 10;

    charts.forEach((chart, index) => {
      const columnIndex = index % 3;
      if (index > 0 && columnIndex === 0) {
        y = addPdfPageIfNeeded(doc, y + cardHeight + 8, 70);
      }
      const x = startX + columnIndex * (cardWidth + gap);
      const items = chart.items || [];
      const maxValue = Math.max(
        chart.maxValue || 0,
        chart.threshold || 0,
        ...items.map((item) => Number(item.value) || 0),
        1
      );
      const axisLabels = getChartAxisLabels(chart);

      doc.setDrawColor(216, 222, 232);
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(x, y - 4, cardWidth, cardHeight, 1.5, 1.5, "S");
      doc.setTextColor(0, 31, 82);
      doc.setFontSize(7);
      doc.setFont(undefined, "bold");
      doc.text(String(chart.title), x + 3, y + 1, { maxWidth: cardWidth - 6 });
      doc.setFont(undefined, "normal");

      if (chart.type === "vertical") {
        const { axisMax, ticks } = getChartAxisScale(maxValue);
        const chartTop = y + 10;
        const chartBottom = y + 44;
        const chartLeft = x + 13;
        const chartRight = x + cardWidth - 4;
        const barMaxHeight = chartBottom - chartTop;
        doc.setTextColor(100, 116, 144);
        doc.setFontSize(4.7);
        doc.text(axisLabels.y, x + 3, chartTop - 2, { maxWidth: 14 });
        ticks.forEach((tick, tickIndex) => {
          const tickY = chartTop + (tickIndex / Math.max(ticks.length - 1, 1)) * barMaxHeight;
          doc.text(String(tick), x + 4, tickY + 1.2, { align: "left" });
        });
        doc.setDrawColor(216, 222, 232);
        doc.line(chartLeft, chartTop, chartLeft, chartBottom);
        doc.line(chartLeft, chartBottom, chartRight, chartBottom);
        if (chart.threshold) {
          const thresholdY = chartBottom - (chart.threshold / axisMax) * barMaxHeight;
          doc.setDrawColor(239, 68, 68);
          doc.setLineDashPattern([1, 1], 0);
          doc.line(chartLeft, thresholdY, chartRight, thresholdY);
          doc.setLineDashPattern([], 0);
        }
        items.slice(0, Number(chart.maxItems) > 0 ? Number(chart.maxItems) : 4).forEach((item, itemIndex, visibleItems) => {
          const barHeight = Math.max(2, ((Number(item.value) || 0) / axisMax) * barMaxHeight);
          const slot = (chartRight - chartLeft) / Math.max(visibleItems.length, 1);
          const barX = chartLeft + slot * itemIndex + Math.max(0, (slot - 6) / 2);
          doc.setFillColor(...getChartColor(chart, item));
          doc.rect(barX, chartBottom - barHeight, 6, barHeight, "F");
          doc.setTextColor(100, 116, 144);
          doc.setFontSize(5);
          doc.text(String(item.label), barX - 4, chartBottom + 4, { maxWidth: 15 });
        });
        doc.setTextColor(100, 116, 144);
        doc.setFontSize(4.7);
        doc.text(axisLabels.x, (chartLeft + chartRight) / 2, y + 55, { align: "center" });
      } else if (chart.type === "donut") {
        const segments = getDonutSegments(items);
        const total = segments.reduce((sum, item) => sum + item.value, 0);
        const donutImage = createDonutChartDataUrl(chart);
        const donutCenterX = x + 17;
        const donutCenterY = y + 27;

        if (donutImage) {
          doc.addImage(donutImage, "PNG", donutCenterX - 12, donutCenterY - 12, 24, 24);
        } else {
          doc.setFillColor(248, 250, 252);
          doc.circle(donutCenterX, donutCenterY, 12, "F");
        }
        doc.setFillColor(255, 255, 255);
        doc.circle(donutCenterX, donutCenterY, 6.6, "F");
        doc.setTextColor(0, 31, 82);
        doc.setFontSize(11);
        doc.setFont(undefined, "bold");
        doc.text(String(total), donutCenterX, y + 26, { align: "center" });
        doc.setFontSize(5);
        doc.text("Total", donutCenterX, y + 30, { align: "center" });
        doc.setFont(undefined, "normal");
        segments.forEach((item, itemIndex) => {
          const rowY = y + 15 + itemIndex * 6;
          doc.setFillColor(...hexToRgb(item.color));
          doc.circle(x + 34, rowY - 1, 1, "F");
          doc.setTextColor(0, 31, 82);
          doc.setFontSize(5.5);
          doc.text(`${item.label} - ${item.value} (${item.percent}%)`, x + 37, rowY, {
            maxWidth: 16,
          });
        });
      } else {
        const { axisMax } = getChartAxisScale(maxValue);
        const chartLeft = x + 4;
        const chartRight = x + cardWidth - 4;
        const trackWidth = chartRight - chartLeft;
        const axisTicks = [0, axisMax / 2, axisMax].map(formatChartTick);
        let itemY = y + 13;
        items.slice(0, 5).forEach((item) => {
          const percent = Math.min(100, ((Number(item.value) || 0) / axisMax) * 100);
          doc.setTextColor(0, 31, 82);
          doc.setFontSize(5.6);
          doc.setFont(undefined, "bold");
          doc.text(String(item.label), chartLeft, itemY, { maxWidth: 35 });
          doc.text(String(item.value), chartRight, itemY, { align: "right" });
          doc.setFont(undefined, "normal");
          doc.setFillColor(238, 242, 247);
          doc.roundedRect(chartLeft, itemY + 2, trackWidth, 1.8, 0.9, 0.9, "F");
          doc.setFillColor(...getChartColor(chart, item));
          doc.roundedRect(chartLeft, itemY + 2, (trackWidth * percent) / 100, 1.8, 0.9, 0.9, "F");
          itemY += 8;
        });
        doc.setTextColor(100, 116, 144);
        doc.setFontSize(4.7);
        axisTicks.forEach((tick, tickIndex) => {
          const tickX = chartLeft + (tickIndex / 2) * (chartRight - chartLeft);
          doc.text(String(tick), tickX, y + 49, { align: tickIndex === 0 ? "left" : tickIndex === 2 ? "right" : "center" });
        });
      }
    });

    return y + cardHeight + 10;
  };

  const generateDocx = async (data) => {
    const sections = getReportSections(data);
    const details = getPreviewDetails(data);
    const metrics = getPreviewMetrics(data);
    const charts = getPreviewCharts(data);
    const logoBuffer = await loadImageArrayBuffer(RouteLogo);
    const chartImageBuffers = charts.map((chart) =>
      dataUrlToUint8Array(createReportChartDataUrl(chart))
    );
    const border = {
      style: BorderStyle.SINGLE,
      size: 1,
      color: "D8DEE8",
    };
    const noneBorder = {
      style: BorderStyle.NONE,
      size: 0,
      color: "E8EAF6",
    };
    const noBorders = {
      top: noneBorder,
      bottom: noneBorder,
      left: noneBorder,
      right: noneBorder,
      insideHorizontal: noneBorder,
      insideVertical: noneBorder,
    };
    const cellBorders = {
      top: border,
      bottom: border,
      left: border,
      right: border,
    };
    const textParagraph = (text, options = {}) =>
      new Paragraph({
        alignment: options.alignment,
        spacing: { before: options.before || 0, after: options.after || 0 },
        children: [
          new TextRun({
            text: String(text ?? ""),
            bold: options.bold,
            size: options.size || 18,
            color: options.color || "000000",
            font: options.font || "Inter",
          }),
        ],
      });
    const makeCell = (children, options = {}) =>
      new DocxTableCell({
        children,
        width: options.width
          ? { size: options.width, type: WidthType.PERCENTAGE }
          : undefined,
        shading: { fill: options.fill || "FFFFFF" },
        margins: {
          top: options.marginY || 130,
          bottom: options.marginY || 130,
          left: options.marginX || 160,
          right: options.marginX || 160,
        },
        borders: options.noBorder ? noBorders : cellBorders,
      });
    const spacer = (height = 140) =>
      new Paragraph({ spacing: { before: height, after: 0 } });

    const headerTable = new DocxTable({
      width: { size: 100, type: WidthType.PERCENTAGE },
      borders: noBorders,
      rows: [
        new DocxTableRow({
          children: [
            makeCell(
              [
                textParagraph(getReportTitle(data).replace(" Preview", ""), {
                  bold: true,
                  size: 28,
                  color: "001F52",
                  font: "Roboto",
                }),
              ],
              { width: 76, fill: "FFFFFF", noBorder: true, marginY: 160, marginX: 0 }
            ),
            makeCell(
              [
                new Paragraph({
                  alignment: AlignmentType.RIGHT,
                  children: logoBuffer
                    ? [
                        new ImageRun({
                          data: logoBuffer,
                          type: "png",
                          transformation: { width: 112, height: 33 },
                        }),
                      ]
                    : [
                        new TextRun({
                          text: "RouteEye",
                          bold: true,
                          size: 20,
                          color: "183059",
                        }),
                      ],
                }),
              ],
              { width: 24, fill: "FFFFFF", noBorder: true, marginY: 140, marginX: 0 }
            ),
          ],
        }),
      ],
    });

    const detailRows = [];
    for (let index = 0; index < details.length; index += 3) {
      const rowDetails = details.slice(index, index + 3);
      detailRows.push(
        new DocxTableRow({
          children: [0, 1, 2].map((slot) => {
            const detail = rowDetails[slot];
            return makeCell(
              detail
                ? [
                    textParagraph(detail.label, {
                      size: 13,
                      color: "647490",
                      bold: true,
                      after: 40,
                    }),
                    textParagraph(detail.value || "N/A", {
                      size: 16,
                      color: "000000",
                      bold: true,
                    }),
                  ]
                : [textParagraph("")],
              { width: 33.33, fill: "FFFFFF", marginY: 150, marginX: 180 }
            );
          }),
        })
      );
    }
    const detailTable = new DocxTable({
      width: { size: 100, type: WidthType.PERCENTAGE },
      rows: detailRows.length
        ? detailRows
        : [
            new DocxTableRow({
              children: [
                makeCell(
                  [
                    textParagraph("No report details available", {
                      size: 14,
                      color: "647490",
                    }),
                  ],
                  { width: 100 }
                ),
              ],
            }),
          ],
    });

    const metricTable = metrics.length
      ? new DocxTable({
          width: { size: 100, type: WidthType.PERCENTAGE },
          rows: [
            new DocxTableRow({
              children: metrics.slice(0, 4).map((metric) =>
                makeCell(
                  [
                    textParagraph(metric.label, {
                      size: 12,
                      color: "64748B",
                      bold: true,
                      after: 40,
                    }),
                    textParagraph(`${metric.value}${metric.unit ? ` ${metric.unit}` : ""}`, {
                      size: 24,
                      color: "001F52",
                      bold: true,
                    }),
                  ],
                  { width: 25, fill: "F8FAFC", marginY: 150, marginX: 180 }
                )
              ),
            }),
          ],
        })
      : null;

    const chartTables = [];
    for (let chartIndex = 0; chartIndex < charts.length; chartIndex += 3) {
      const chartCells = charts.slice(chartIndex, chartIndex + 3).map((chart, localIndex) => {
        const imageBuffer = chartImageBuffers[chartIndex + localIndex];
        const children = imageBuffer
          ? [
              new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [
                  new ImageRun({
                    data: imageBuffer,
                    type: "png",
                    transformation: { width: 195, height: 112 },
                  }),
                ],
              }),
            ]
          : [textParagraph(chart.title, { size: 16, bold: true, color: "111827" })];
        return makeCell(children, {
          width: 33.33,
          fill: "FFFFFF",
          marginY: 90,
          marginX: 90,
        });
      });
      while (chartCells.length < 3) {
        chartCells.push(makeCell([textParagraph("")], { width: 33.33 }));
      }
      chartTables.push(
        new DocxTable({
          width: { size: 100, type: WidthType.PERCENTAGE },
          rows: [new DocxTableRow({ children: chartCells })],
        })
      );
    }

    const sectionContent = sections.flatMap((section) => {
      const tableRows = [
        new DocxTableRow({
          children: section.columns.map((col) =>
            makeCell(
              [
                textParagraph(col.label, {
                  size: 14,
                  color: "183059",
                  bold: true,
                }),
              ],
              { fill: "B7BFCC", marginY: 130, marginX: 150 }
            )
          ),
        }),
        ...section.data.map(
          (row, rowIndex) =>
            new DocxTableRow({
              children: section.columns.map((col) =>
                makeCell(
                  [
                    textParagraph(getExportCellValue(row[col.key]), {
                      size: 14,
                      color: "414958",
                    }),
                  ],
                  {
                    fill: rowIndex % 2 === 0 ? "FFFFFF" : "F9F9F9",
                    marginY: 120,
                    marginX: 150,
                  }
                )
              ),
            })
        ),
      ];

      return [
        spacer(180),
        textParagraph(section.sectionTitle || data.reportType, {
          bold: true,
          size: 18,
          color: "183059",
          after: 100,
        }),
        new DocxTable({
          rows: tableRows,
          width: { size: 100, type: WidthType.PERCENTAGE },
        }),
      ];
    });

    const children = [
      headerTable,
      detailTable,
      ...(metricTable ? [spacer(120), metricTable] : []),
      spacer(180),
      ...chartTables.flatMap((table) => [table, spacer(120)]),
      ...sectionContent,
    ];

    const doc = new Document({
      sections: [
        {
          properties: {
            page: {
              margin: {
                top: 720,
                right: 720,
                bottom: 720,
                left: 720,
              },
            },
          },
          children,
        },
      ],
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, `${data.reportType}_report.docx`);
  };

  const generatePDF = async (data) => {
    const sections = getReportSections(data);
    const charts = getPreviewCharts(data);
    const logoDataUrl = await loadImageAsDataUrl(RouteLogo);
    const doc = new jsPDF();

    doc.setFontSize(14);
    doc.setFont(undefined, "bold");
    doc.setTextColor(0, 31, 82);
    doc.text(getReportTitle(data).replace(" Preview", ""), 13, 17);
    if (logoDataUrl) {
      doc.addImage(logoDataUrl, "PNG", 171, 12, 25, 7.2);
    }
    doc.setFont(undefined, "normal");

    let startY = drawPdfPreviewDetails(doc, data, 30);
    startY = drawPdfFilterRow(doc, data, startY);
    startY = drawPdfMetrics(doc, data, startY);
    startY = drawPdfCharts(doc, charts, startY);

    sections.forEach((section) => {
      startY = addPdfPageIfNeeded(doc, startY, 28);
      doc.setTextColor(0, 31, 82);
      doc.setFontSize(10);
      doc.setFont(undefined, "bold");
      doc.text(section.sectionTitle || data.reportType, 13, startY);
      doc.setFont(undefined, "normal");
      const head = [section.columns.map((col) => col.label)];
      const body = section.data.map((row) =>
        section.columns.map((col) => getExportCellValue(row[col.key]))
      );

      autoTable(doc, {
        head,
        body,
        startY: startY + 5,
        theme: "plain",
        margin: { left: 13, right: 13 },
        headStyles: {
          fillColor: [183, 191, 204],
          textColor: [24, 48, 89],
          fontStyle: "bold",
          fontSize: 8,
        },
        bodyStyles: {
          textColor: [65, 73, 88],
          fontSize: 8,
          lineColor: [238, 238, 238],
          lineWidth: 0.2,
        },
        alternateRowStyles: {
          fillColor: [249, 249, 249],
        },
        styles: {
          cellPadding: 2.5,
          overflow: "linebreak",
        },
      });
      startY = (doc.lastAutoTable?.finalY || startY) + 12;
    });

    doc.save(`${data.reportType}_report.pdf`);
  };

  const generateExcel = async (data) => {
    const sections = getReportSections(data);
    const details = getPreviewDetails(data);
    const metrics = getPreviewMetrics(data);
    const charts = getPreviewCharts(data);
    const logoBuffer = await loadImageArrayBuffer(RouteLogo);
    const chartImageBuffers = charts.map((chart) =>
      dataUrlToUint8Array(createReportChartDataUrl(chart))
    );
    const zip = new JSZip();
    const rows = [];
    const merges = ["A1:C4", "D1:I4", "J1:L4"];
    const drawingImages = [];
    const drawingAnchors = [];
    const imagePixelToEmu = (pixels) => Math.round(pixels * 9525);
    const colName = (index) => {
      let name = "";
      let value = index;
      while (value > 0) {
        const remainder = (value - 1) % 26;
        name = String.fromCharCode(65 + remainder) + name;
        value = Math.floor((value - 1) / 26);
      }
      return name;
    };
    const cellRef = (col, row) => `${colName(col)}${row}`;
    const cell = (col, row, value = "", style = 0) =>
      `<c r="${cellRef(col, row)}" s="${style}" t="inlineStr"><is><t>${escapeHtml(value)}</t></is></c>`;
    const blankCell = (col, row, style = 0) =>
      `<c r="${cellRef(col, row)}" s="${style}"/>`;
    const rowXml = (rowNumber, cells, height) =>
      `<row r="${rowNumber}"${height ? ` ht="${height}" customHeight="1"` : ""}>${cells.join("")}</row>`;
    const mergedCell = (startCol, endCol, rowNumber, value, style) => {
      if (startCol !== endCol) {
        merges.push(`${cellRef(startCol, rowNumber)}:${cellRef(endCol, rowNumber)}`);
      }
      return cell(startCol, rowNumber, value, style);
    };
    const addDrawingImage = (buffer, anchor) => {
      if (!buffer) return;
      const imageNumber = drawingImages.length + 1;
      const relId = `rId${imageNumber}`;
      drawingImages.push({ buffer, name: `image${imageNumber}.png`, relId });
      drawingAnchors.push({
        ...anchor,
        relId,
        id: imageNumber + 1,
        name: anchor.name || `Report Chart ${imageNumber}`,
      });
    };

    if (logoBuffer) {
      addDrawingImage(logoBuffer, {
        name: "RouteEye Logo",
        col: 10,
        row: 0,
        colOff: 90000,
        rowOff: 90000,
        widthPx: 120,
        heightPx: 35,
      });
    }

    for (let rowNumber = 1; rowNumber <= 4; rowNumber += 1) {
      const headerCells = Array.from({ length: 12 }, (_, index) =>
        blankCell(index + 1, rowNumber, 1)
      );
      if (rowNumber === 1) {
        headerCells[0] = cell(1, rowNumber, getReportTitle(data).replace(" Preview", ""), 2);
      }
      rows.push(rowXml(rowNumber, headerCells, rowNumber === 1 ? 24 : 18));
    }

    let currentRow = 6;
    for (let index = 0; index < details.length; index += 3) {
      const labelRow = currentRow;
      const valueRow = currentRow + 1;
      const labelCells = [];
      const valueCells = [];
      details.slice(index, index + 3).forEach((detail, cardIndex) => {
        const startCol = cardIndex * 4 + 1;
        const endCol = startCol + 3;
        merges.push(`${cellRef(startCol, labelRow)}:${cellRef(endCol, labelRow)}`);
        merges.push(`${cellRef(startCol, valueRow)}:${cellRef(endCol, valueRow)}`);
        labelCells.push(cell(startCol, labelRow, detail.label, 4));
        valueCells.push(cell(startCol, valueRow, detail.value || "N/A", 5));
      });
      rows.push(rowXml(labelRow, labelCells, 18));
      rows.push(rowXml(valueRow, valueCells, 22));
      currentRow += 3;
    }

    if (metrics.length) {
      currentRow += 1;
      const labelRow = currentRow;
      const valueRow = currentRow + 1;
      const labelCells = [];
      const valueCells = [];
      metrics.slice(0, 4).forEach((metric, metricIndex) => {
        const startCol = metricIndex * 3 + 1;
        const endCol = startCol + 2;
        merges.push(`${cellRef(startCol, labelRow)}:${cellRef(endCol, labelRow)}`);
        merges.push(`${cellRef(startCol, valueRow)}:${cellRef(endCol, valueRow)}`);
        labelCells.push(cell(startCol, labelRow, metric.label, 4));
        valueCells.push(
          cell(
            startCol,
            valueRow,
            `${metric.value}${metric.unit ? ` ${metric.unit}` : ""}`,
            5
          )
        );
      });
      rows.push(rowXml(labelRow, labelCells, 18));
      rows.push(rowXml(valueRow, valueCells, 24));
      currentRow += 3;
    }

    if (charts.length) {
      currentRow += 1;
      merges.push(`${cellRef(1, currentRow)}:${cellRef(12, currentRow)}`);
      rows.push(rowXml(currentRow, [cell(1, currentRow, "Performance Breakdown", 10)], 24));
      currentRow += 1;

      for (let chunkStart = 0; chunkStart < charts.length; chunkStart += 3) {
        const chartChunk = charts.slice(chunkStart, chunkStart + 3);
        const chartTopRow = currentRow;
        chartChunk.forEach((chart, chartIndex) => {
          addDrawingImage(chartImageBuffers[chunkStart + chartIndex], {
            name: chart.title || `Report Chart ${chunkStart + chartIndex + 1}`,
            col: chartIndex * 4,
            row: chartTopRow - 1,
            colOff: 70000,
            rowOff: 50000,
            widthPx: 310,
            heightPx: 179,
          });
        });

        for (let rowOffset = 0; rowOffset < 11; rowOffset += 1) {
          const rowNumber = currentRow;
          rows.push(
            rowXml(
              rowNumber,
              Array.from({ length: 12 }, (_, index) => blankCell(index + 1, rowNumber)),
              18
            )
          );
          currentRow += 1;
        }
        currentRow += 1;
      }
      currentRow += 2;
    }

    sections.forEach((section) => {
      const columns = section.columns || [];
      const sectionRows = section.data || [];
      merges.push(`${cellRef(1, currentRow)}:${cellRef(12, currentRow)}`);
      rows.push(
        rowXml(currentRow, [cell(1, currentRow, section.sectionTitle || data.reportType, 10)], 24)
      );
      currentRow += 1;

      const baseSpan = Math.max(1, Math.floor(12 / Math.max(columns.length, 1)));
      let startCol = 1;
      const headerCells = columns.map((column, columnIndex) => {
        const endCol = columnIndex === columns.length - 1 ? 12 : startCol + baseSpan - 1;
        const output = mergedCell(startCol, endCol, currentRow, column.label, 7);
        startCol = endCol + 1;
        return output;
      });
      rows.push(rowXml(currentRow, headerCells, 22));
      currentRow += 1;

      sectionRows.forEach((row, rowIndex) => {
        startCol = 1;
        const rowCells = columns.map((column, columnIndex) => {
          const endCol = columnIndex === columns.length - 1 ? 12 : startCol + baseSpan - 1;
          const output = mergedCell(
            startCol,
            endCol,
            currentRow,
            getExportCellValue(row[column.key]),
            rowIndex % 2 === 0 ? 8 : 9
          );
          startCol = endCol + 1;
          return output;
        });
        rows.push(rowXml(currentRow, rowCells, 20));
        currentRow += 1;
      });
      currentRow += 2;
    });

    const stylesXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
      <styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
        <fonts count="7">
          <font><sz val="11"/><color rgb="FF000000"/><name val="Inter"/></font>
          <font><b/><sz val="16"/><color rgb="FF183059"/><name val="Inter"/></font>
          <font><b/><sz val="9"/><color rgb="FF183059"/><name val="Inter"/></font>
          <font><b/><sz val="8"/><color rgb="FF647490"/><name val="Inter"/></font>
          <font><b/><sz val="10"/><color rgb="FF000000"/><name val="Inter"/></font>
          <font><b/><sz val="9"/><color rgb="FF183059"/><name val="Inter"/></font>
          <font><sz val="8"/><color rgb="FF414958"/><name val="Inter"/></font>
        </fonts>
        <fills count="10">
          <fill><patternFill patternType="none"/></fill>
          <fill><patternFill patternType="gray125"/></fill>
          <fill><patternFill patternType="solid"><fgColor rgb="FFE8EAF6"/><bgColor indexed="64"/></patternFill></fill>
          <fill><patternFill patternType="solid"><fgColor rgb="FFFFFFFF"/><bgColor indexed="64"/></patternFill></fill>
          <fill><patternFill patternType="solid"><fgColor rgb="FFB7BFCC"/><bgColor indexed="64"/></patternFill></fill>
          <fill><patternFill patternType="solid"><fgColor rgb="FFF9F9F9"/><bgColor indexed="64"/></patternFill></fill>
          <fill><patternFill patternType="solid"><fgColor rgb="FF2563EB"/><bgColor indexed="64"/></patternFill></fill>
          <fill><patternFill patternType="solid"><fgColor rgb="FF22C55E"/><bgColor indexed="64"/></patternFill></fill>
          <fill><patternFill patternType="solid"><fgColor rgb="FFF59E0B"/><bgColor indexed="64"/></patternFill></fill>
          <fill><patternFill patternType="solid"><fgColor rgb="FFEEF2F7"/><bgColor indexed="64"/></patternFill></fill>
        </fills>
        <borders count="2">
          <border><left/><right/><top/><bottom/><diagonal/></border>
          <border>
            <left style="thin"><color rgb="FFD8DEE8"/></left>
            <right style="thin"><color rgb="FFD8DEE8"/></right>
            <top style="thin"><color rgb="FFD8DEE8"/></top>
            <bottom style="thin"><color rgb="FFD8DEE8"/></bottom>
            <diagonal/>
          </border>
        </borders>
        <cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
        <cellXfs count="16">
          <xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
          <xf numFmtId="0" fontId="0" fillId="9" borderId="0" xfId="0" applyFill="1"/>
          <xf numFmtId="0" fontId="1" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
          <xf numFmtId="0" fontId="2" fillId="2" borderId="0" xfId="0" applyFont="1" applyFill="1" applyAlignment="1"><alignment horizontal="right" vertical="center" wrapText="1"/></xf>
          <xf numFmtId="0" fontId="3" fillId="3" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1"/>
          <xf numFmtId="0" fontId="4" fillId="3" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1"/>
          <xf numFmtId="0" fontId="5" fillId="3" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1"/>
          <xf numFmtId="0" fontId="5" fillId="4" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1"/>
          <xf numFmtId="0" fontId="6" fillId="3" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1"/>
          <xf numFmtId="0" fontId="6" fillId="5" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1"/>
          <xf numFmtId="0" fontId="5" fillId="0" borderId="0" xfId="0" applyFont="1"/>
          <xf numFmtId="0" fontId="0" fillId="6" borderId="0" xfId="0" applyFill="1"/>
          <xf numFmtId="0" fontId="0" fillId="7" borderId="0" xfId="0" applyFill="1"/>
          <xf numFmtId="0" fontId="0" fillId="8" borderId="0" xfId="0" applyFill="1"/>
          <xf numFmtId="0" fontId="0" fillId="2" borderId="0" xfId="0" applyFill="1"/>
          <xf numFmtId="0" fontId="5" fillId="3" borderId="0" xfId="0" applyFont="1" applyFill="1"/>
        </cellXfs>
        <cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
      </styleSheet>`;

    const sheetXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
      <worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"
        xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
        <dimension ref="A1:L${Math.max(currentRow, 1)}"/>
        <sheetViews><sheetView workbookViewId="0" showGridLines="0"/></sheetViews>
        <cols>${Array.from({ length: 12 }, (_, index) => `<col min="${index + 1}" max="${index + 1}" width="12" customWidth="1"/>`).join("")}</cols>
        <sheetData>${rows.join("")}</sheetData>
        <mergeCells count="${merges.length}">${merges.map((merge) => `<mergeCell ref="${merge}"/>`).join("")}</mergeCells>
        ${drawingImages.length ? '<drawing r:id="rId3"/>' : ""}
      </worksheet>`;

    const drawingXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
      <xdr:wsDr xmlns:xdr="http://schemas.openxmlformats.org/drawingml/2006/spreadsheetDrawing"
        xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main">
        ${drawingAnchors
          .map(
            (anchor) => `
              <xdr:oneCellAnchor>
                <xdr:from>
                  <xdr:col>${anchor.col}</xdr:col>
                  <xdr:colOff>${anchor.colOff || 0}</xdr:colOff>
                  <xdr:row>${anchor.row}</xdr:row>
                  <xdr:rowOff>${anchor.rowOff || 0}</xdr:rowOff>
                </xdr:from>
                <xdr:ext cx="${imagePixelToEmu(anchor.widthPx)}" cy="${imagePixelToEmu(anchor.heightPx)}"/>
                <xdr:pic>
                  <xdr:nvPicPr><xdr:cNvPr id="${anchor.id}" name="${escapeHtml(anchor.name)}"/><xdr:cNvPicPr/></xdr:nvPicPr>
                  <xdr:blipFill>
                    <a:blip xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships" r:embed="${anchor.relId}"/>
                    <a:stretch><a:fillRect/></a:stretch>
                  </xdr:blipFill>
                  <xdr:spPr><a:prstGeom prst="rect"><a:avLst/></a:prstGeom></xdr:spPr>
                </xdr:pic>
                <xdr:clientData/>
              </xdr:oneCellAnchor>
            `
          )
          .join("")}
      </xdr:wsDr>`;

    zip.file("[Content_Types].xml", `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
      <Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
        <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
        <Default Extension="xml" ContentType="application/xml"/>
        <Default Extension="png" ContentType="image/png"/>
        <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
        <Override PartName="/xl/worksheets/sheet1.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>
        <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
        ${drawingImages.length ? '<Override PartName="/xl/drawings/drawing1.xml" ContentType="application/vnd.openxmlformats-officedocument.drawing+xml"/>' : ""}
      </Types>`);
    zip.file("_rels/.rels", `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
      <Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
        <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
      </Relationships>`);
    zip.file("xl/workbook.xml", `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
      <workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"
        xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
        <sheets><sheet name="Report" sheetId="1" r:id="rId1"/></sheets>
      </workbook>`);
    zip.file("xl/_rels/workbook.xml.rels", `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
      <Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
        <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet1.xml"/>
        <Relationship Id="rId2" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
        ${drawingImages.length ? '<Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/drawing" Target="drawings/drawing1.xml"/>' : ""}
      </Relationships>`);
    zip.file("xl/worksheets/sheet1.xml", sheetXml);
    zip.file("xl/styles.xml", stylesXml);
    if (drawingImages.length) {
      zip.file("xl/drawings/drawing1.xml", drawingXml);
      zip.file("xl/drawings/_rels/drawing1.xml.rels", `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
        <Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
          ${drawingImages
            .map(
              (image) =>
                `<Relationship Id="${image.relId}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/image" Target="../media/${image.name}"/>`
            )
            .join("")}
        </Relationships>`);
      zip.file("xl/worksheets/_rels/sheet1.xml.rels", `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
        <Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
          <Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/drawing" Target="../drawings/drawing1.xml"/>
        </Relationships>`);
      drawingImages.forEach((image) => {
        zip.file(`xl/media/${image.name}`, image.buffer);
      });
    }

    const blob = await zip.generateAsync({
      type: "blob",
      mimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, `${data.reportType}_report.xlsx`);
  };

  const handleExport = async () => {
    if (!reportData) return;
    switch (exportFormat) {
      case 'docx':
        await generateDocx(reportData);
        break;
      case 'pdf':
        await generatePDF(reportData);
        break;
      case 'csv':
        await generateExcel(reportData);
        break;
      default:
        break;
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: 'white',
        borderRadius: 1,
        padding: { xs: '8px 12px', sm: '10px 18px', md: '8px 20px' },
        height: { xs: 'auto', sm: '50px', md: '60px' },
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        alignItems: 'center',
        gap: { xs: 1, sm: 1.5, md: 2 },
        width: { xs: '100%', sm: 'auto' },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 1, sm: 1.5, md: 2 },
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          justifyContent: { xs: 'center', sm: 'flex-start' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Radio
            checked={exportFormat === 'pdf'}
            onChange={() => setExportFormat('pdf')}
            value="pdf"
            sx={{
              color: '#183059',
              '&.Mui-checked': { color: '#20A8E0' },
              padding: 0,
              '& .MuiSvgIcon-root': { fontSize: { xs: '16px', sm: '18px', md: '20px' } },
            }}
          />
          <img
            src={Pdf}
            alt="PDF"
            style={{
              width: window.innerWidth < 600 ? 18 : window.innerWidth < 900 ? 20 : 24,
              height: window.innerWidth < 600 ? 18 : window.innerWidth < 900 ? 20 : 24,
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Radio
            checked={exportFormat === 'csv'}
            onChange={() => setExportFormat('csv')}
            value="csv"
            sx={{
              color: '#183059',
              '&.Mui-checked': { color: '#20A8E0' },
              padding: 0,
              '& .MuiSvgIcon-root': { fontSize: { xs: '16px', sm: '18px', md: '20px' } },
            }}
          />
          <img
            src={Csv}
            alt="Excel"
            style={{
              width: window.innerWidth < 600 ? 18 : window.innerWidth < 900 ? 20 : 24,
              height: window.innerWidth < 600 ? 18 : window.innerWidth < 900 ? 20 : 24,
            }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Radio
            checked={exportFormat === 'docx'}
            onChange={() => setExportFormat('docx')}
            value="docx"
            sx={{
              color: '#183059',
              '&.Mui-checked': { color: '#20A8E0' },
              padding: 0,
              '& .MuiSvgIcon-root': { fontSize: { xs: '16px', sm: '18px', md: '20px' } },
            }}
          />
          <img
            src={Word}
            alt="DOCX"
            style={{
              width: window.innerWidth < 600 ? 18 : window.innerWidth < 900 ? 20 : 24,
              height: window.innerWidth < 600 ? 18 : window.innerWidth < 900 ? 20 : 24,
            }}
          />
        </Box>
      </Box>
      <Button
        variant="contained"
        onClick={handleExport}
        sx={{
          gap: { xs: '4px', sm: '8px', md: '10px' },
          borderRadius: '8px',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: '#414958',
          paddingTop: { xs: '4px', sm: '6px', md: '8px' },
          paddingRight: { xs: '8px', sm: '10px', md: '12px' },
          paddingBottom: { xs: '4px', sm: '6px', md: '8px' },
          paddingLeft: { xs: '8px', sm: '10px', md: '12px' },
          backgroundColor: '#F9E719',
          color: 'black',
          fontSize: { xs: '8px', sm: '9px', md: '14px' },
          textTransform: 'none',
          '&:hover': { backgroundColor: '#FFD700' },
        }}
      >
        <img
          src={Download}
          alt="download"
          style={{
            marginRight: '8px',
            width: window.innerWidth < 600 ? '14px' : '16px',
            height: window.innerWidth < 600 ? '14px' : '16px',
          }}
        />
        Download
      </Button>
    </Box>
  );
};

export default ExportOptions;
